/**
 * The play space, as two vocabularies: how much room a body has, and what stands in for a jump.
 *
 * **Why these live in `src/shared` rather than in `src/interaction`.** They are chosen on a parent
 * screen, carried on `SessionLaunchConfig`, and read by the camera layer — three layers, so the
 * words have to sit below all of them. Nothing here describes a body: two string unions, no
 * landmarks, no measurements, no thresholds (D-060(7) holds — landmark types never leave
 * `src/interaction`). `CameraMode` continues to be re-exported from `interaction/camera/readiness`
 * so existing call sites are unchanged.
 *
 * The axis is deliberately **not** `presentationMode` (D-063(2)): architecture §5's presentation
 * mode is a property of the *game*, and this is a property of the *room*.
 */

/** GDS §6's two play spaces. Compact ~1–1.5 m, Full-Motion ~1.5–2.5 m. */
export type CameraMode = 'compact' | 'full-motion';

export const CAMERA_MODES: readonly CameraMode[] = ['full-motion', 'compact'] as const;

/**
 * GDS §15's substitute set, verbatim: "Replace jumping with: Hands raised · Large reaches ·
 * Alternating arm raises."
 *
 * A set rather than a single option because GDS §11 lists "Jump substitute" among the things a
 * parent *selects*, and a child who cannot raise both arms above their head may still be able to
 * reach, or to lift one arm at a time. Whichever is chosen, "the learning mechanic remains
 * counting" (GDS §15) — every one of them produces exactly the same count onsets the jump does.
 */
export type JumpSubstitute = 'hands-raised' | 'large-reach' | 'alternating-arms';

export const JUMP_SUBSTITUTES: readonly JumpSubstitute[] = [
  'hands-raised',
  'large-reach',
  'alternating-arms',
] as const;

export function isCameraMode(value: unknown): value is CameraMode {
  return typeof value === 'string' && (CAMERA_MODES as readonly string[]).includes(value);
}

export function isJumpSubstitute(value: unknown): value is JumpSubstitute {
  return typeof value === 'string' && (JUMP_SUBSTITUTES as readonly string[]).includes(value);
}
